import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EXPERIMENT_ELIGIBILTY_PRECHECK_FAILED } from 'constants/events';
import { getFeatureFlagCreator } from 'creators/featureFlag';
import * as analytics from 'utilities/analytics';
import * as logger from 'utilities/loggerx';
import { store } from 'utilities/store';
import { getParam } from 'utilities/url';

/**
 * @param {object} args
 * @param {string} args.flag Name of the feature flag
 * @param {array} args.activationValues An array containing all activation values
 * @param {function} [args.eligibilityPreCheckFunction] An optional function to determine eligibilty requirements for this experiment. If supplied it
 * must return an object shaped like the following:
 * {pass: true|false, failReason: 'reason why the preCheck failed'}
 * @param {boolean} [args.skipLoggingToSegment] An optional parameter to skip logging to segment
 * @param {object} [args.extraProperties] Extra properties to attach to the standard LD payload that can be used for flag assignments
 * @param {object} [args.returnAssignmentName] Optional parameter to return the name of the assignment
 */
export function useFlagIsActivated({
  flag,
  activationValues,
  eligibilityPreCheckFunction,
  skipLoggingToSegment,
  extraProperties,
  returnAssignmentName = false,
}) {
  const dispatch = useDispatch();
  const currentFlag = useSelector(state => state.feature_flags[flag]);
  const flagUrlParam = getParam(flag);
  // Allows us to do a deep compare of the entire activation values string
  const activationValuesString = JSON.stringify(activationValues);
  const extraPropertiesRef = useRef(extraProperties);

  const checkFlagForActivationValue = useCallback(
    currentFlag => {
      if (!currentFlag || !currentFlag.fetched) {
        return undefined;
      }

      const result = [].concat(activationValues).includes(currentFlag.value);

      if (returnAssignmentName) {
        return [result, currentFlag.value];
      }

      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activationValuesString]
  );

  // see if flag is already fetched and set initial state
  const [isActive, setIsActive] = useState(() => {
    return checkFlagForActivationValue(currentFlag);
  });

  useEffect(
    () => setIsActive(checkFlagForActivationValue(currentFlag)),
    [checkFlagForActivationValue, currentFlag]
  );

  useEffect(() => {
    if (eligibilityPreCheckFunction) {
      const preCheckResult = eligibilityPreCheckFunction();
      const pass = preCheckResult?.pass;
      const failReason = preCheckResult?.failReason;

      if (pass === undefined || failReason === undefined) {
        logger.error(
          `Eligibility pre-check function should return an object with 'pass' and 'failReason' keys`
        );

        return;
      }

      if (!pass) {
        if (!skipLoggingToSegment) {
          analytics.track(EXPERIMENT_ELIGIBILTY_PRECHECK_FAILED, {
            flag,
            failReason,
          });
        }

        return;
      }
    }

    dispatch(getFeatureFlagCreator(flag, extraPropertiesRef.current));
  }, [dispatch, flag, eligibilityPreCheckFunction]);

  if (flagUrlParam) {
    let flagValue = flagUrlParam;

    if (flagValue === 'true') {
      flagValue = true;
    }

    if (flagValue === 'false') {
      flagValue = false;
    }

    if (returnAssignmentName) {
      return [activationValues.includes(flagValue), flagUrlParam];
    }

    return activationValues.includes(flagValue);
  }

  return isActive;
}

// get feature flag value
export function useFeatureFlagValue(flag) {
  // see if flag is already fetched and set initial state
  const [flagValue, setFlagValue] = useState(() => {
    const { feature_flags: featureFlags } = store.getState();

    return getFlagValue(featureFlags[flag]);
  });

  function getFlagValue(currentFlag) {
    if (!currentFlag || !currentFlag.fetched) {
      return undefined;
    }

    return currentFlag.value;
  }

  useEffect(() => {
    const unsubHandle = store.subscribe(() => {
      const { feature_flags: featureFlags } = store.getState();

      setFlagValue(getFlagValue(featureFlags[flag]));
    });

    store.dispatch(getFeatureFlagCreator(flag));

    return unsubHandle;
  }, [flag]);

  // Ignore the LD value if a matching URL param was found
  const flagUrlParam = getParam(flag);

  if (flagUrlParam) {
    let returnValue = flagUrlParam;

    if (returnValue === 'true') {
      returnValue = true;
    }

    if (returnValue === 'false') {
      returnValue = false;
    }

    return returnValue;
  }

  return flagValue;
}
